import { getGuidsToSkip, isOrgUiEnabled, isWelcomeEmailEnabled, } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import { useCallback, useMemo, useState, } from 'react';
import { PrincipalVisibilityType } from "../../../../../services/generated/graphql-types";
import { useCreateUserMutation, useGetUserAccessibleOrgsQuery, useGetUserAndGroupsQuery, useSendWelcomeEmailMutation, } from "../../../../../services/user-group/user-group-service";
import { hasCreateEditInputError, } from '../../../../user-management/users/util';
import { captureFailureEvent, CHECKBOX_SECTION_PAGINATION_BATCHSIZE, } from '../../../../user-management/util';
import { classifyServerError } from '../../server-errors';
import { useAdminUserTracking } from '../../shared';
const INITIAL_FORM = {
    email: '',
    name: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    allowOthersToShare: true,
    sendWelcomeEmail: true,
    welcomeEmailMessage: '',
};
const INITIAL_ERRORS = {
    isNameRequired: false,
    isDisplayNameRequired: false,
    isNameLong: false,
    isDisplayNameLong: false,
    isNotWhitelistedDomainEmail: false,
    isEmailEmpty: false,
    arePasswordsNotMatching: false,
    isPasswordEmpty: false,
    isConfirmPasswordEmpty: false,
    isPasswordValidationRuleFailed: false,
    isNameEmailNotSame: false,
};
const VALIDATE_ALL_FIELDS = {
    isNameUntouched: false,
    isDisplayNameUntouched: false,
    isPasswordUntouched: false,
    isConfirmPasswordUntouched: false,
    isEmailUntouched: false,
};
const FIELD_ERROR_MAP = {
    email: ['isEmailEmpty', 'isNotWhitelistedDomainEmail'],
    name: ['isNameRequired', 'isNameLong', 'isNameEmailNotSame'],
    displayName: ['isDisplayNameRequired', 'isDisplayNameLong'],
    password: [
        'isPasswordEmpty',
        'isPasswordValidationRuleFailed',
        'arePasswordsNotMatching',
    ],
    confirmPassword: ['isConfirmPasswordEmpty', 'arePasswordsNotMatching'],
};
export const useCreateUserForm = (currentOrgId, onSuccess, assignedGroupCount = 0, assignedOrgCount = 0) => {
    const tracking = useAdminUserTracking();
    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [errors, setErrors] = useState({ ...INITIAL_ERRORS });
    const [serverFieldErrors, setServerFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [groupSearchText, setGroupSearchText] = useState('');
    const { data: userAndGroupsData, loading: groupsLoading, } = useGetUserAndGroupsQuery(groupSearchText, getGuidsToSkip(), CHECKBOX_SECTION_PAGINATION_BATCHSIZE);
    const isClusterAdmin = isOrgUiEnabled() && currentOrgId === -1;
    const { data: accessibleOrgsData, loading: orgsLoading, } = useGetUserAccessibleOrgsQuery(!isClusterAdmin);
    const { createUser } = useCreateUserMutation();
    const { sendWelcomeEmail: sendWelcomeEmailFn, } = useSendWelcomeEmailMutation();
    const groupsList = userAndGroupsData?.getUserGroupsPrincipalList?.objects || [];
    const orgsList = isClusterAdmin
        ? accessibleOrgsData?.Data?.orgs || []
        : [];
    const updateField = useCallback((key, value) => {
        const updatedForm = { ...form, [key]: value };
        setForm(updatedForm);
        const errorKeysForField = FIELD_ERROR_MAP[key];
        if (errorKeysForField) {
            const revalidated = hasCreateEditInputError({ ...INITIAL_ERRORS }, {
                name: updatedForm.name.trim(),
                displayName: updatedForm.displayName.trim(),
                email: updatedForm.email.trim(),
                password: updatedForm.password,
                confirmPassword: updatedForm.confirmPassword,
                allowOthersToShare: updatedForm.allowOthersToShare,
            }, false, VALIDATE_ALL_FIELDS);
            const fieldErrors = {};
            errorKeysForField.forEach(k => {
                fieldErrors[k] = revalidated[k];
            });
            setErrors(prev => ({ ...prev, ...fieldErrors }));
        }
        if (key === 'name' && serverFieldErrors.name) {
            setServerFieldErrors(prev => ({ ...prev, name: undefined }));
        }
        setSubmitError(null);
    }, [form, serverFieldErrors.name]);
    const actions = useMemo(() => ({
        setEmail: (value) => updateField('email', value),
        setName: (value) => updateField('name', value),
        setDisplayName: (value) => updateField('displayName', value),
        setPassword: (value) => updateField('password', value),
        setConfirmPassword: (value) => updateField('confirmPassword', value),
        setAllowOthersToShare: (value) => updateField('allowOthersToShare', value),
        setSendWelcomeEmail: (value) => updateField('sendWelcomeEmail', value),
        setWelcomeEmailMessage: (value) => updateField('welcomeEmailMessage', value),
    }), [updateField]);
    const validateStep1 = useCallback(() => {
        const validationResult = hasCreateEditInputError({ ...INITIAL_ERRORS }, {
            name: form.name.trim(),
            displayName: form.displayName.trim(),
            email: form.email.trim(),
            password: form.password,
            confirmPassword: form.confirmPassword,
            allowOthersToShare: form.allowOthersToShare,
        }, false, VALIDATE_ALL_FIELDS);
        setErrors(validationResult);
        return !Object.values(validationResult).some(Boolean);
    }, [form]);
    const handleSubmit = useCallback(async (selectedGroupIds, selectedOrgIds) => {
        setIsSubmitting(true);
        setSubmitError(null);
        setServerFieldErrors({});
        const isClusterCtx = currentOrgId === -1;
        const canSendWelcomeEmail = !isOktaEnabled() &&
            currentOrgId !== -1 &&
            isWelcomeEmailEnabled();
        const sendInviteEmail = canSendWelcomeEmail && Boolean(form.sendWelcomeEmail);
        const creationMode = sendInviteEmail
            ? 'invite'
            : 'manual';
        tracking.createSubmitted({
            creationMode,
            authType: 'local',
            assignedOrgCount: isClusterCtx
                ? selectedOrgIds.size
                : assignedOrgCount,
            assignedGroupCount: selectedGroupIds.size || assignedGroupCount,
            assignedRoleCount: 0,
            hasDisplayName: Boolean(form.displayName.trim()),
            sendInviteEmail,
        });
        const createStartedAt = Date.now();
        const payload = {
            name: form.name.trim(),
            displayname: form.displayName.trim(),
            visibility: form.allowOthersToShare
                ? PrincipalVisibilityType.Default
                : PrincipalVisibilityType.NonSharable,
            groups: [...selectedGroupIds],
            properties: {
                mail: form.email.trim(),
                frontendURL: window.location.hostname,
            },
            isAllOrgContext: isClusterCtx,
            ...(form.password && { password: form.password }),
            ...(isOrgUiEnabled() &&
                isClusterCtx && {
                orgids: [...selectedOrgIds],
            }),
        };
        let createdUserId;
        try {
            const result = await createUser(payload);
            createdUserId = result.data?.Session__createUser?.id;
        }
        catch (err) {
            const classified = classifyServerError(err);
            if (classified.isDuplicateName) {
                setServerFieldErrors({ name: classified.message });
                captureFailureEvent('CREATE_USER', classified.rawErrorObj, classified.message);
                tracking.createFailed({
                    errorCode: classified.code,
                    errorMessage: classified.message,
                    failedStep: 'credentials',
                    creationMode,
                });
                setIsSubmitting(false);
                return { ok: false, rewindToStep: 0 };
            }
            setSubmitError(classified.message);
            captureFailureEvent('CREATE_USER', classified.rawErrorObj, classified.message);
            tracking.createFailed({
                errorCode: classified.code,
                errorMessage: classified.message,
                failedStep: 'submit',
                creationMode,
            });
            setIsSubmitting(false);
            return { ok: false };
        }
        let welcomeEmailFailed = false;
        if (canSendWelcomeEmail && form.sendWelcomeEmail && createdUserId) {
            try {
                await sendWelcomeEmailFn(createdUserId, form.welcomeEmailMessage, false);
            }
            catch (err) {
                welcomeEmailFailed = true;
                const classified = classifyServerError(err);
                captureFailureEvent('SEND_WELCOME_EMAIL', classified.rawErrorObj, classified.message);
            }
        }
        tracking.createSucceeded({
            targetUserId: createdUserId ?? '',
            creationDurationMs: Date.now() - createStartedAt,
            creationMode,
        });
        if (sendInviteEmail && createdUserId) {
            tracking.userInvited({
                userID: createdUserId,
                isReinvite: false,
                isBulk: false,
                userCount: 1,
            });
        }
        onSuccess(welcomeEmailFailed);
        setIsSubmitting(false);
        return { ok: true };
    }, [
        form,
        currentOrgId,
        createUser,
        sendWelcomeEmailFn,
        onSuccess,
        tracking,
        assignedGroupCount,
        assignedOrgCount,
    ]);
    const resetForm = useCallback(() => {
        setForm({ ...INITIAL_FORM });
        setErrors({ ...INITIAL_ERRORS });
        setServerFieldErrors({});
        setIsSubmitting(false);
        setSubmitError(null);
        setGroupSearchText('');
    }, []);
    return {
        form,
        actions,
        errors,
        serverFieldErrors,
        setServerFieldErrors,
        isSubmitting,
        submitError,
        validateStep1,
        handleSubmit,
        resetForm,
        groupsData: {
            list: groupsList,
            loading: groupsLoading,
            searchText: groupSearchText,
            setSearchText: setGroupSearchText,
        },
        orgsData: {
            list: orgsList,
            loading: orgsLoading,
        },
    };
};
//# sourceMappingURL=useCreateUserForm.js.map